import { Link } from "@inertiajs/react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"

import type { AgingBadOrder, AgingInShop } from "./types"

// The drill-downs under the tiles: the two lists somebody actually works from.
// The first is the coordination backlog (flagged, not yet at a shop); the
// second is dwell (at a shop, not yet back). They are disjoint on purpose —
// a car appears in exactly one of them.

const WAITING_LABELS: Record<string, string> = {
  lessor: "Lessor",
  fra: "FRA",
  shop_transit: "Shop (in transit)",
  shop: "Shop",
  mru: "MRU crew",
  internal: "Us",
}

function waitingLabel(value: string | null): string {
  if (!value) return "—"
  return WAITING_LABELS[value] ?? value
}

// Dates arrive as ISO strings from Rails. A missing follow-up is a dash, never
// "never" — we don't know that.
//
// timeZone: "UTC" is REQUIRED, not cosmetic. A Rails `date` parses as UTC
// midnight, and rendering that in a US zone walks it back a day — so the same
// POD read "Aug 6" on the dashboard and "Aug 7" on the work-order page (which
// pins UTC in work_orders/status-chip.tsx#formatDate). Two screens disagreeing
// about a promised date is the fastest way to lose an operator's trust in both.
export function shortDate(value: string | null): string {
  if (!value) return "—"
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
}

function Empty({ children }: { children: string }) {
  return <p className="text-muted-foreground py-6 text-sm">{children}</p>
}

export function OldestBadOrdersTable({ rows }: { rows: AgingBadOrder[] }) {
  const routes = useOrgRoutes()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Waiting longest</CardTitle>
        <p className="text-muted-foreground text-sm">
          Bad ordered, not yet at a shop
        </p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <Empty>Nothing is waiting. </Empty>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Car</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead>Defect</TableHead>
                  <TableHead>Waiting on</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead className="text-right">Last follow-up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      <Link
                        href={routes.workOrderPath(row.id)}
                        className="hover:underline"
                      >
                        {row.car ?? "—"}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.facility ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.defect ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {waitingLabel(row.waiting_on)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.days_waiting ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right tabular-nums">
                      {shortDate(row.last_followed_up_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function LongestInShopTable({ rows }: { rows: AgingInShop[] }) {
  const routes = useOrgRoutes()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Longest in shop</CardTitle>
        <p className="text-muted-foreground text-sm">
          At a shop now, against the promised-out date
        </p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <Empty>No cars are at a shop.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Car</TableHead>
                  <TableHead>Shop</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead className="text-right">Promised out</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      <Link
                        href={routes.workOrderPath(row.id)}
                        className="hover:underline"
                      >
                        {row.car ?? "—"}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.shop ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.days_in_shop ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {/* A missed promise is called out in words, not only in
                          red — the badge carries the meaning either way. */}
                      {row.pod_missed ? (
                        <Badge variant="destructive">
                          Missed {shortDate(row.promised_on)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {shortDate(row.promised_on)}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
